import { useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Send, Loader2, Mic, Paperclip, X } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

const formatDuration = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getFileIcon = (fileType) => {
  if (fileType?.startsWith('image/')) return '🖼️';
  if (fileType?.includes('pdf')) return '📄';
  if (fileType?.includes('word') || fileType?.includes('document')) return '📝';
  if (fileType?.includes('excel') || fileType?.includes('spreadsheet')) return '📊';
  if (fileType?.includes('powerpoint') || fileType?.includes('presentation')) return '📽️';
  if (fileType?.includes('zip') || fileType?.includes('rar')) return '📦';
  return '📎';
};

export const MessageInput = ({
  messageInput,
  setMessageInput,
  sending,
  onSendMessage,
  onTyping,
  // Voice recording
  isRecording,
  recordingDuration,
  audioBlob,
  onStartRecording,
  onStopRecording,
  onCancelRecording,
  onSendVoiceMessage,
  // File upload
  selectedFile,
  filePreview,
  fileCaption,
  setFileCaption,
  uploadingFile,
  onFileSelect,
  onCancelFileUpload,
  onSendFileMessage
}) => {
  const { t } = useLanguage();
  const fileInputRef = useRef(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (messageInput.trim()) {
      onSendMessage(e);
    }
  };

  return (
    <div className="p-4 border-t border-slate-800">
      {/* Voice Recording UI */}
      {(isRecording || audioBlob) && (
        <div className="mb-3 p-3 bg-slate-800 rounded-lg flex items-center gap-3">
          {isRecording ? (
            <>
              <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
              <span className="text-sm text-slate-300">{t('recording')} {formatDuration(recordingDuration)}</span>
              <div className="flex-1" />
              <Button size="sm" variant="ghost" onClick={onCancelRecording}>
                {t('cancel')}
              </Button>
              <Button size="sm" onClick={onStopRecording} className="bg-red-500 hover:bg-red-600">
                {t('stop_recording')}
              </Button>
            </>
          ) : audioBlob && (
            <>
              <Mic className="w-5 h-5 text-pink-500" />
              <span className="text-sm text-slate-300">{t('voice_message')} ({formatDuration(recordingDuration)})</span>
              <div className="flex-1" />
              <Button size="sm" variant="ghost" onClick={onCancelRecording}>
                {t('cancel')}
              </Button>
              <Button size="sm" onClick={onSendVoiceMessage} className="bg-pink-500 hover:bg-pink-600" disabled={sending}>
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : t('send_message')}
              </Button>
            </>
          )}
        </div>
      )}
      
      {/* File Upload Preview */}
      {selectedFile && (
        <div className="mb-3 p-3 bg-slate-800 rounded-lg">
          <div className="flex items-center gap-3">
            {filePreview ? (
              <img src={filePreview} alt="Preview" className="w-16 h-16 rounded-lg object-cover" />
            ) : (
              <div className="w-16 h-16 rounded-lg bg-slate-700 flex items-center justify-center text-2xl">
                {getFileIcon(selectedFile.type)}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate text-slate-200">{selectedFile.name}</p>
              <p className="text-xs text-slate-400">{formatFileSize(selectedFile.size)}</p>
              <Input
                placeholder={t('add_caption')}
                value={fileCaption}
                onChange={(e) => setFileCaption(e.target.value)}
                className="mt-2 bg-slate-700 border-slate-600 text-sm h-8"
              />
            </div>
            <div className="flex flex-col gap-2">
              <Button size="sm" variant="ghost" onClick={onCancelFileUpload}>
                <X className="w-4 h-4" />
              </Button>
              <Button size="sm" onClick={onSendFileMessage} className="bg-pink-500 hover:bg-pink-600" disabled={uploadingFile}>
                {uploadingFile ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,.rar,.jpg,.jpeg,.png,.gif,.webp"
        onChange={onFileSelect}
      />
      
      <form onSubmit={handleSubmit} className="flex gap-2">
        {/* File Attach Button */}
        <Button 
          type="button"
          variant="ghost"
          onClick={() => fileInputRef.current?.click()}
          className="text-slate-400 hover:text-pink-400"
          disabled={isRecording || !!audioBlob || !!selectedFile}
          data-testid="file-attach-btn"
        >
          <Paperclip className="w-5 h-5" />
        </Button>
        
        <Input
          placeholder={t('type_message')}
          value={messageInput}
          onChange={(e) => {
            setMessageInput(e.target.value);
            if (e.target.value) {
              onTyping();
            }
          }}
          className="flex-1 bg-slate-800 border-slate-700"
          disabled={sending || isRecording || !!selectedFile}
          data-testid="message-input"
        />
        
        {/* Voice Record Button */}
        {!messageInput.trim() && !isRecording && !audioBlob && !selectedFile && (
          <Button 
            type="button"
            variant="ghost"
            onClick={onStartRecording}
            className="text-slate-400 hover:text-pink-400"
            data-testid="voice-record-btn"
          >
            <Mic className="w-5 h-5" />
          </Button>
        )}
        
        <Button 
          type="submit" 
          disabled={!messageInput.trim() || sending || isRecording || !!selectedFile}
          className="bg-pink-500 hover:bg-pink-600"
          data-testid="send-message-btn"
        >
          {sending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Send className="w-4 h-4" />
          )}
        </Button>
      </form>
    </div>
  );
};

export default MessageInput;
